
import { defineComponent, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Info as InfoIcon, Search as SearchIcon, ArrowLeft as ResetIcon, ArrowUp, ArrowDown, ChevronRight, CaretDown } from '@vicons/fa';
import BreedingListDialog from '@/components/BreedingListModal.vue';
import BreedingInternalDialog from '@/components/BreedingInternalModal.vue';
import BreedingMountDialog from '@/components/BreedingMountModal.vue';
import { HoundModel, FindAllResponse, formatNumberDecimals, emptyAddress } from '../../../common';

import { http, EthersService as eth, convertToUSD } from '../services';
import { externalBreedingFeeCurrency, breedingSeasonActive, defaultPageSize, exchangeRatesAvailable, exchangeRates } from '@/data/global';
import breakpoints from '../utils/breakpoints';

enum BreedingTypes {
  INTERNAL,
  LIST
}

enum SortTypes {
  FEE='breedingFee',
  TOTAL_RUNS='totalRuns'
}

enum OrderTypes {
  ASC='ASC',
  DESC='DESC'
}

export default defineComponent({
  name: 'Breeding',
  components: {
    InfoIcon,
    SearchIcon,
    ResetIcon,
    ArrowUp,
    ArrowDown,
    BreedingListDialog,
    BreedingInternalDialog,
    BreedingMountDialog,
    ChevronRight,
    CaretDown
  },
  setup () {
    const page = ref(1);
    const pageSize = ref(defaultPageSize.value);

    const hounds = ref<Array<HoundModel>>([]);
    const totalHounds = ref(0);

    const breedingType = ref(BreedingTypes.INTERNAL);

    const currencyName = eth.currenciesInfo.find(c => c.value === externalBreedingFeeCurrency.value)?.label;

    let commitedFilters = {
      title: '',
      sort: null,
      order: OrderTypes.ASC,
      gender: undefined,
      totalRuns: null,
      fee: [null, null]
    };
    const filtersModel = reactive({ ...commitedFilters });

    const search = () => {
      page.value = 1;
      commitedFilters = { ...filtersModel };
      resetHounds();
    };

    const resetHounds = (): void => {
      http.getHounds({
        name: commitedFilters.title,
        limit: pageSize.value,
        offset: (page.value - 1) * pageSize.value,
        orderBy: commitedFilters.sort ? `${commitedFilters.sort}-${commitedFilters.order}` : 'lastBreed-DESC',
        sex: commitedFilters.gender,
        availableToBreed: true,
        running: false,
        hasLibido: true,
        minRuns: commitedFilters.totalRuns,
        minFee: commitedFilters.fee[0] ? eth.utils.parseUnits(commitedFilters.fee[0].toString(), 'ether').toString() : null,
        maxFee: commitedFilters.fee[1] ? eth.utils.parseUnits(commitedFilters.fee[1].toString(), 'ether').toString() : null
      }).then((res: FindAllResponse<HoundModel>) => {
        hounds.value = [];
        res.data.forEach(e => {
          hounds.value.push(new HoundModel(e));
        });
        totalHounds.value = res.total;
      })
    };

    const resetFilters = (): void => {
      filtersModel.title = '';
      filtersModel.sort = null;
      filtersModel.gender = undefined;
      filtersModel.totalRuns = null;
      filtersModel.fee[0] = null;
      filtersModel.fee[1] = null;
      search();
    };

    if (eth.initialized) resetHounds();
    watch(() => eth.userAddress, (_) => resetHounds());
    const changePagination = (ev: number, size: boolean = false): void => {
      if (size) {
        pageSize.value = ev;
        page.value = 1;
      }
      resetHounds();
    };

    const showListModal = ref(false);
    const showInternalModal = ref(false);
    const showMountModal = ref(false);
    const mountId = ref<number | null>(null);
    const mountTokenId = ref<string | null>(null);
    const mountFee = ref<string | null>(null);

    const openListModal = () => {
      if (breedingType.value === BreedingTypes.LIST) {
        showListModal.value = true;
      }
      if (breedingType.value === BreedingTypes.INTERNAL) {
        showInternalModal.value = true;
      }
    };

    const mount = (id: number, token_id: string, fee: string) => {
      mountId.value = id;
      mountTokenId.value = token_id;
      mountFee.value = fee;
      showMountModal.value = true;
    }

    const openDiscord = () => {
      window.open('https://discord.com/invite/N8AnA662Mx');
    };

    const router = useRouter();
    const openHound = (id: string) => {
      const targetRoute = router.resolve({
        name: 'Hound',
        params: {
          id: id
        }
      }).fullPath;
      const basePath = window.location.origin;
      window.open(basePath + targetRoute);
    };

    const rerouteToRacing = () => {
      if (router.options.history.state.back) router.back();
      else router.push('/racing');
    };

    return {
      breedingSeasonActive,
      openDiscord,
      ethReactive: eth.reactive,
      hounds,
      totalHounds,
      page,
      pageSize,
      breedingType,
      breedingTypes: [
        {
          value: BreedingTypes.INTERNAL,
          label: 'Internal Breeding'
        },
        {
          value: BreedingTypes.LIST,
          label: 'List on Marketplace'
        }
      ],
      sortTypes: [
        {
          value: SortTypes.FEE,
          label: 'Breeding Fee'
        },
        {
          value: SortTypes.TOTAL_RUNS,
          label: 'Total Runs'
        }
      ],
      genderTypes: [
        {
          value: null,
          label: 'Any'
        },
        {
          value: 1,
          label: 'Male'
        },
        {
          value: 2,
          label: 'Female'
        }
      ],
      filtersModel,
      showListModal,
      showInternalModal,
      showMountModal,
      search,
      changePagination,
      resetFilters,
      openListModal,
      mount,
      mountId,
      mountFee,
      mountTokenId,
      formatNumberDecimals,
      resetHounds,
      eth,
      currencyName,
      openHound,
      convertToUSD,
      exchangeRatesAvailable,
      exchangeRates,
      emptyAddress,
      externalBreedingFeeCurrency,
      rerouteToRacing,
      breakpoints
    }
  }
});
